import { allDevices } from './devices';
import {
	BASE_TEMPLATE_ID,
	createBaseTemplate,
	createContentFromTemplate,
} from './templates';
import {
	CountdownSettings,
	DisplayRules,
	StickyBannerSettingsPayload,
} from './types';

export const DEFAULT_TEMPLATE = BASE_TEMPLATE_ID;

/** Fonts loaded from Google Fonts when selected. */
export const GOOGLE_FONTS = [
	'Poppins',
	'Roboto',
	'Inter',
	'Open Sans',
	'Montserrat',
	'Lato',
	'Source Sans 3',
];

/**
 * Weights each Google Font really has. Asking Google Fonts for a missing
 * weight makes the whole stylesheet request fail. Empty = single weight.
 */
export const GOOGLE_FONT_WEIGHTS: Record< string, string > = {
	Lato: '100;300;400;700;900',
	'Open Sans': '300;400;500;600;700;800',
	'Share Tech': '',
};

/** Weights requested for fonts not listed in GOOGLE_FONT_WEIGHTS. */
export const DEFAULT_GOOGLE_FONT_WEIGHTS = '300;400;500;600;700;800;900';

/** Fonts that are expected to exist on the visitor's device. */
export const SYSTEM_FONTS = [ 'Arial', 'Helvetica', 'Georgia', 'Verdana' ];

export const FONT_WEIGHT_OPTIONS = [
	{ value: '300', label: 'Light (300)' },
	{ value: '400', label: 'Normal (400)' },
	{ value: '500', label: 'Medium (500)' },
	{ value: '600', label: 'SemiBold (600)' },
	{ value: '700', label: 'Bold (700)' },
	{ value: '800', label: 'ExtraBold (800)' },
	{ value: '900', label: 'Black (900)' },
];

export const defaultCountdown: CountdownSettings = {
	endDate: '',
	endTime: '',
	timezone: 'site',
	afterExpiry: 'hide',
};

export const DEFAULT_PRIORITY = 10;

export const defaultDisplayRules: DisplayRules = {
	showOnAllPages: true,
	hiddenPageIds: [],
	visitors: {
		loginState: 'all',
		roles: [],
		visitorType: 'all',
	},
	devices: allDevices(),
	schedule: {
		enabled: false,
		start: '',
		end: '',
		days: [ 0, 1, 2, 3, 4, 5, 6 ],
	},
	placement: {
		position: 'top',
		behavior: 'static',
		pushContent: true,
		zIndex: 9999,
	},
	trigger: {
		type: 'immediate',
		delaySeconds: 3,
		scrollPercent: 25,
	},
	closeBehavior: {
		rememberHours: 0,
	},
};

export const WEEK_DAYS = [
	{ value: 1, short: 'Mon' },
	{ value: 2, short: 'Tue' },
	{ value: 3, short: 'Wed' },
	{ value: 4, short: 'Thu' },
	{ value: 5, short: 'Fri' },
	{ value: 6, short: 'Sat' },
	{ value: 0, short: 'Sun' },
];

/**
 * Fresh (deep-copied) default banner, safe to mutate.
 *
 * @param design Template whose sample texts fill the banner (base by default).
 */
export const createDefaultSettings = (
	design = createBaseTemplate()
): StickyBannerSettingsPayload =>
	JSON.parse(
		JSON.stringify( {
			id: '',
			name: 'New banner',
			enabled: false,
			priority: DEFAULT_PRIORITY,
			templateId: design.id,
			content: createContentFromTemplate( design ),
			countdown: defaultCountdown,
			displayRules: defaultDisplayRules,
		} )
	);
